import React, { useEffect, useState } from "react";
import ObjectUtils, { coalesce, sanitizeValue } from "../../../../../utils/ObjectUtils";
import { findAnswer, makeAnswer } from "../../../../../utils/QuestionUtils";

import PropTypes from "prop-types";
import { InputGroup } from "react-bootstrap";
import FormControl from "react-bootstrap/FormControl";
import { useIntl } from "react-intl";
import ValidatorUtils, { LENGTH_TYPE } from "../../../../../utils/ValidatorUtils";
import QuestionValidation from "../QuestionValidation";

const { getDeepProperty } = ObjectUtils;

/**
 * Text question component for survey-view.
 */
const TextRenderer = ({
  disabled,
  question,
  collection,
  setQuestionAnswer,
  row,
  rows,
  column,
  className,
  isIncomplete,
  showLengthValidation
}) => {
  const intl = useIntl();
  const answer = findAnswer(collection, {
    row,
    column
  });
  const textValue = sanitizeValue(coalesce(getDeepProperty(answer, "value"), ""));
  const type = coalesce(getDeepProperty(column, "type"), question.type, "text").toLowerCase();
  const suffix = coalesce(getDeepProperty(column, "suffix"), question.suffix);
  const lengthValidator = coalesce(getDeepProperty(question, "validators"), []).find(
    (validator) =>
      validator.validatorType === LENGTH_TYPE &&
      (!column || !validator.columnName || validator.columnName === column.name)
  );
  const configuredMaxLength = getDeepProperty(lengthValidator, "props", "length");
  const hasConfiguredMaxLength =
    configuredMaxLength !== null &&
    configuredMaxLength !== undefined &&
    String(configuredMaxLength).trim() !== "";
  const numericMaxLength = Number(configuredMaxLength);
  const maxLength =
    hasConfiguredMaxLength && Number.isFinite(numericMaxLength) && numericMaxLength >= 0
      ? numericMaxLength
      : undefined;

  const [value, setValue] = useState(textValue);
  const [lengthLimitReached, setLengthLimitReached] = useState(false);

  useEffect(() => {
    setValue(textValue?.length ? (type === "number" ? ValidatorUtils.getIntlNumberInput(textValue, intl) : textValue) : "");
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [textValue]);

  const handleChange = (event) => {
    let text = event.target.value;
    if (text !== "" && type === "number") {
      if (!ValidatorUtils.isNumber(intl, text)) {
        event.preventDefault();
        return;
      }
    }
    if (type === "text" && Number.isFinite(maxLength) && text.length > maxLength && text.length > value.length) {
      setLengthLimitReached(true);
      return;
    }
    setLengthLimitReached(false);
    setValue(text);
  };

  const handleFocus = (e) => {
    if (type === "number") {
      setValue((val) => ValidatorUtils.getStandardNumberInput(val, intl));
    }
  };

  const handlePaste = (e) => {
    const data = e.clipboardData.getData("text");
    if (type === "number") {
      const number = ValidatorUtils.formatNumberInput(intl, data);
      if (Number.isNaN(parseFloat(number))) {
        e.preventDefault();
      } else {
        e.clipboardData.setData("text", number);
      }
    }
  };

  const handleBlur = () => {
    if (type === "number") {
      setValue((val) => {
        const number = ValidatorUtils.formatNumberInput(intl, val);
        setQuestionAnswer(question, makeAnswer(number, row, column));
        return ValidatorUtils.getIntlNumberInput(val, intl);
      });
    } else {
      setQuestionAnswer(question, makeAnswer(value, row, column));
    }
  };

  return (
    <>
    <InputGroup className={`${type === "number" && !row ? "w-50" : ""} ${isIncomplete ? "border border-2 border-danger" : ""}`}>
      {rows ? (
        <FormControl
          as="textarea"
          className={className}
          style={{ minHeight: `calc( 1rem * ${rows} )` }}
          value={value}
          onPaste={handlePaste}
          onKeyDown={type === "number" ? ValidatorUtils.handleNumberInput : undefined}
          disabled={disabled}
          placeholder={
            type === "number"
              ? intl.formatMessage({ id: "user.surveyView.type.number" })
              : intl.formatMessage({ id: "user.surveyView.type.text" })
          }
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
        />
      ) : (
        <FormControl
          value={value}
          onKeyDown={type === "number" ? ValidatorUtils.handleNumberInput : undefined}
          disabled={disabled}
          placeholder={
            type === "number"
              ? intl.formatMessage({ id: "user.surveyView.type.number" })
              : intl.formatMessage({ id: "user.surveyView.type.text" })
          }
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
        />
      )}
      {suffix && (
          <InputGroup.Text>{suffix}</InputGroup.Text>
      )}
    </InputGroup>
    {showLengthValidation && lengthValidator && Number.isFinite(maxLength) && (
      <div className="text-end">
        <QuestionValidation
          validator={lengthValidator}
          validation={{
            isOk: value.length <= maxLength,
            currentLength: value.length,
            length: maxLength
          }}
          questionType={type}
        />
      </div>
    )}
    {lengthLimitReached && (
      <div className="text-danger text-end" role="alert" aria-live="polite">
        {intl.formatMessage({ id: "app.validation.maxLengthReached" }, { length: maxLength })}
      </div>
    )}
    </>
  );
};

TextRenderer.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object.isRequired,
  collection: PropTypes.array,
  setQuestionAnswer: PropTypes.func,
  row: PropTypes.object,
  rows: PropTypes.number,
  column: PropTypes.object,
  intl: PropTypes.object,
  className: PropTypes.string,
  isIncomplete: PropTypes.bool,
  showLengthValidation: PropTypes.bool,
};

export default React.memo(TextRenderer);
